import { useEffect, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { intl } from "LocaleWrapper";
import { useLazyQuery, useMutation } from "@apollo/client";
import { Button, FormGroup, Textarea } from "@cofano/cck";
import EditIcon from "@mui/icons-material/Edit";
import FormField from "components/cck/FormField";
import { useConfirm } from "components/hooks/useConfirm";
import { FormDialog } from "components/dialogs/Dialogs";
import { Language, usePermission } from "imports/Utils";
import { gql } from "__generated__/gql";
import "./styling.scss";
import "../../gql/fragments";
import { LastShippedProductsProps } from "components/products/LastShippedProducts";
import LastShippedProductsTable from "view/ships/components/table/LastShippedProductsTable";
import { BargeType } from "__generated__/enums";
import { Grid2 as Grid, Typography } from "@mui/material";

const LastShippedProductsFreeText = ({ charterId, voyage, ship, refetch, labelPlacement = "left-center", forceReadOnly }: LastShippedProductsProps) => {

	const { isReadOnly, isAdmin, isCofanoAdmin, isCompanyUser, isMainUserLimited } = usePermission();

	const [ products, setProducts ] = useState<string[]>([]);

	const getLastShippedProducts: () => string[] = () => {
		if (voyage?.lastShippedProductsFreeText) {
			return voyage.lastShippedProductsFreeText as string[];
		} else {
			return [];
		}
	};

	useEffect(() => {
		setProducts(getLastShippedProducts());
	}, [ voyage, ship ]);

	const [ dialogOpen, openDialog ] = useState(false);

	return (
		<div>
			<span style={{ alignItems: "center", display: "flex", marginBottom: 8 }}>
				<Typography variant="h4" sx={{ fontSize: 13, fontWeight: 600 }} color="textPrimary">
					{intl.formatMessage({ id: "RECENTLY_SHIPPED_PRODUCTS" })}
				</Typography>

				{(voyage && !forceReadOnly && !isReadOnly && (isAdmin || isCofanoAdmin || isCompanyUser || isMainUserLimited)) && (
					<Button
						onClick={() => openDialog(true)}
						icon={<EditIcon fontSize="small" />}
						style={{ marginLeft: 8 }}
						variant="text"
						size="small" />
				)}
			</span>

			<div className="last-shipped-products">
				{products.length > 0 ? (
					products.map((product, i) => {
						return <>
							<FormGroup
								labelPlacement={labelPlacement}
								labelWidth={240}
								label={intl.formatMessage({ id: `LAST_PRODUCT_${i + 1}` })}
							>
								<span style={{ whiteSpace: "pre-wrap" }}>{product}</span>
							</FormGroup>
						</>;
					})
				) : (
					<div style={{ marginBottom: 8 }}>
						{Language("NONE")}
					</div>
				)}
			</div>

			{voyage && (
				<ProductLastShippedEditDialog
					open={dialogOpen}
					onClose={() => openDialog(false)}
					voyageId={voyage.id}
					products={products}
					refetch={refetch}
					ship={ship}
					charterId={charterId}
				/>
			)}
		</div>
	);
};

type ProductForm = {
	product1?: string | null;
	product2?: string | null;
	product3?: string | null;
};

type ProductLastShippedEditDialogProps = {
	open: boolean;
	onClose: () => void;
	voyageId: number;
	products?: string[];
	refetch: () => void;
	ship?: LastShippedProductsProps["ship"];
	charterId?: number;
};

const ProductLastShippedEditDialog = ({ open, onClose, voyageId, products, refetch, ship, charterId }: ProductLastShippedEditDialogProps) => {
	const confirm = useConfirm();

	const defaultValues = {
		product1: null,
		product2: null,
		product3: null,
	};

	const { control, handleSubmit, formState, reset } = useForm<ProductForm>({
		defaultValues: defaultValues,
	});

	const [ selectedTanks, setSelectedTanks ] = useState<number[]>([]);

	const [ autoFillLastProducts, { loading: loadingLastProducts } ] = useLazyQuery(PREVIOUS_PRODUCTS_SUGGESTION_QUERY, {
		onCompleted: data => {
			reset({
				product1: data.suggestThreeLastShippedProducts[0].map(x => x.product.previousCargoDisplayName || x.product.name).join(", ") || "-",
				product2: data.suggestThreeLastShippedProducts[1].map(x => x.product.previousCargoDisplayName || x.product.name).join(", ") || "-",
				product3: data.suggestThreeLastShippedProducts[2].map(x => x.product.previousCargoDisplayName || x.product.name).join(", ") || "-",
			});
		},
	});

	const [ saveLastShippedProducts, { loading } ] = useMutation(LAST_SHIPPED_PRODUCTS_FREE_TEXT_MUTATION);


	const handleOnClose = () => {
		onClose();
		reset(defaultValues);
	};

	const saveProducts = (data: ProductForm) => {
		const products = [ data.product1, data.product2, data.product3 ].filter(Boolean) as string[];
		saveLastShippedProducts({
			variables: {
				voyageId: voyageId,
				products: products,
			},
		}).then(() => {
			handleOnClose();
			refetch();
		});
	};

	const onSave = (data: ProductForm) => {
		if (!data.product1 || !data.product2 || !data.product3) {
			confirm({
				type: "WARNING",
				title: intl.formatMessage({ id: "LAST_SHIPPED_PRODUCTS_WARNING" }),
			}).then(() => {
				saveProducts(data);
			});
		} else {
			saveProducts(data);
		}
	};

	useEffect(() => {
		if (open) {
			reset({
				product1: products?.[0] as string,
				product2: products?.[1] as string,
				product3: products?.[2] as string,
			});
		}
	}, [ open ]);

	const formField = (name: "product1" | "product2" | "product3") => {
		let label = intl.formatMessage({ id: "PRODUCT" });

		switch (name) {
			case "product1":
				label = intl.formatMessage({ id: "LAST_PRODUCT_1" });
				break;
			case "product2":
				label = intl.formatMessage({ id: "LAST_PRODUCT_2" });
				break;
			case "product3":
				label = intl.formatMessage({ id: "LAST_PRODUCT_3" });
				break;
		}

		return <FormGroup label={label} overflow="hidden">
			<FormField name={name} errors={formState.errors}>
				<Controller
					name={name}
					control={control}
					render={({ field }) =>
						<Textarea
							{...field}
							value={field.value as string || undefined} />
					} />
			</FormField>
		</FormGroup>;
	};

	return <FormDialog
		open={open}
		onClose={handleOnClose}
		title={intl.formatMessage({ id: "RECENTLY_SHIPPED_PRODUCTS" })}
		form='productLastShippedForm'
		loading={loading}
		dialogStyling={{
			content: {
				padding: 0,
			},
		}}
		maxWidth="md"
	>
		<div style={{ padding: 24 }}>
			{ship?.bargeType === BargeType.Tanker && (
				<>
					<div style={{ display: "flex", justifyContent: "space-between", marginTop: 20, marginBottom: 20 }}>
						<Button
							onClick={() => autoFillLastProducts({
								variables: {
									tankIds: selectedTanks,
									charterId: charterId ?? 0,
								} })}
							size="small"
							loading={loadingLastProducts}
							disabled={loadingLastProducts}
						>
							{intl.formatMessage({ id: "AUTO_FILL_LAST_SHIPPED_PRODUCTS" })}
						</Button>

						<div style={{ display: "flex", gap: "12px" }}>
							<Button
								variant="outlined"
								size="small"
								disabled={selectedTanks.length == ship.tanks.length}
								onClick={() => {
									setSelectedTanks(ship.tanks.map(tank => tank.id));
								}}
							>
								{intl.formatMessage({ id: "SELECT_ALL" })}
							</Button>

							<Button
								variant="outlined"
								size="small"
								disabled={selectedTanks.length == 0}
								onClick={() => {
									setSelectedTanks([]);
								}}
							>
								{intl.formatMessage({ id: "DESELECT_ALL" })}
							</Button>
						</div>
					</div>

					<LastShippedProductsTable
						ship={ship}
						setSelectedTanks={setSelectedTanks}
						selectedTanks={selectedTanks}
						filterOnCharterId={charterId}
					/>
				</>
			)}

			<form onSubmit={(event) => void handleSubmit(onSave)(event)} id="productLastShippedForm">
				<Grid container spacing={3}>
					<Grid size={{ xs: 12, md: 4 }}>
						{formField("product1")}
					</Grid>

					<Grid size={{ xs: 12, md: 4 }}>
						{formField("product2")}
					</Grid>

					<Grid size={{ xs: 12, md: 4 }}>
						{formField("product3")}
					</Grid>
				</Grid>
			</form>
		</div>
	</FormDialog>;
};

const LAST_SHIPPED_PRODUCTS_FREE_TEXT_MUTATION = gql(`#graphql
mutation productLastShipped_changeLastShippedProductFreeText($voyageId: Long!, $products: [String!]!) {
	changeLastShippedProductsFreeTextOnVoyage(voyageId: $voyageId, products: $products) {
		id
	}
}
`);

const PREVIOUS_PRODUCTS_SUGGESTION_QUERY = gql(`#graphql
query suggestThreeLastShippedProducts($charterId: Long!, $tankIds: [Long!]!) {
	suggestThreeLastShippedProducts(charterId: $charterId, tankIds: $tankIds) {
		product {
			id
			name
			previousCargoDisplayName
		}
		tanks
	}
}
`);


export default LastShippedProductsFreeText;
